import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShieldCheck, KeyRound, ArrowLeft } from "lucide-react";
import founderApi, { FOUNDER_KEY, errMsg } from "@/lib/founderApi";

const field = "w-full rounded-lg border border-slate-700 bg-slate-950 px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-600";

export default function FounderLogin() {
  const nav = useNavigate();
  const [step, setStep] = useState("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [challenge, setChallenge] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Founder · RideStamp";
    if (sessionStorage.getItem(FOUNDER_KEY)) founderApi.get("/me").then(() => nav("/founder/overview", { replace: true })).catch(() => {});
  }, [nav]);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try {
      if (step === "password") {
        const r = await founderApi.post("/login", { email, password });
        setChallenge(r.data); setStep("code"); setPassword("");
      } else {
        const r = await founderApi.post("/verify", { challenge_id: challenge.challenge_id, code });
        sessionStorage.setItem(FOUNDER_KEY, r.data.token);
        toast.success("Welcome back.");
        nav("/founder/overview", { replace: true });
      }
    } catch (err) {
      const msg = errMsg(err, "Sign-in failed.");
      setError(msg);
      if (/expired|again/i.test(msg) && step === "code") { setStep("password"); setCode(""); }
    } finally { setBusy(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#070b14] px-4" data-testid="founder-login-page">
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(37,99,235,0.18),transparent_55%)]" />
      <form onSubmit={submit} className="relative w-full max-w-sm rounded-2xl border border-slate-800 bg-[#0d1424]/90 p-8 shadow-2xl backdrop-blur-xl" data-testid="founder-login-form">
        <div className="mb-6 flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600/15 text-blue-400">{step === "password" ? <ShieldCheck className="h-5 w-5" /> : <KeyRound className="h-5 w-5" />}</span>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-blue-400">RideStamp</p>
            <h1 className="font-display text-xl font-bold text-white">Founder console</h1>
          </div>
        </div>
        {step === "password" ? (
          <div className="space-y-3">
            <input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className={field} data-testid="founder-email-input" required />
            <input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className={field} data-testid="founder-password-input" required />
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-slate-300" data-testid="founder-code-sent">We emailed a 6-digit code to {challenge?.sent_to?.join(" and ")}. It expires in 10 minutes.</p>
            <input inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder="••••••" className={`${field} text-center font-mono-data text-2xl tracking-[0.5em]`} data-testid="founder-code-input" autoFocus required />
            <button type="button" onClick={() => { setStep("password"); setCode(""); setError(""); }} className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-white" data-testid="founder-code-back">
              <ArrowLeft className="h-3 w-3" /> Start over
            </button>
          </div>
        )}
        {error && <p className="mt-3 text-sm text-red-300" data-testid="founder-login-error">{error}</p>}
        <button type="submit" disabled={busy || (step === "code" && code.length !== 6)} data-testid="founder-login-submit"
          className="mt-5 flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 py-3 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-60">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} {step === "password" ? "Continue" : "Verify & sign in"}
        </button>
        <p className="mt-5 text-center text-[11px] text-slate-600">Private area. Every sign-in is code-verified.</p>
      </form>
    </div>
  );
}
